/**
 * Client and staff roles and the action list `can()` checks (docs/53 §4.3, §4.4; docs/50 §4.11, §5.2).
 * The matrices are data. packages/auth implements `can(principal, action, resource)` over them; the UI only
 * uses them to hide what can't be done. The Worker decides.
 */
import { z } from 'zod';
import { ClientRole, StaffRole } from './enums.ts';

export { ClientRole, StaffRole };

export const CLIENT_ACTIONS = [
  'home.read',
  'activity.read',
  'locations.read',
  'onboarding.read',
  'onboarding.update',
  'golive.approve',
  'facts.read',
  'facts.confirm',
  'domain.check',
  'leads.read',
  'leads.update',
  'leads.export',
  'events.read',
  'reviews.read',
  'reviews.reply',
  'review_requests.read',
  'review_requests.create',
  'review_requests.bulk',
  'review_requests.cancel',
  'review_templates.edit',
  'two_jobs.mark',
  'messaging.read',
  'jobs.read',
  'jobs.create',
  'jobs.update',
  'media.read',
  'media.update',
  'uploads.create',
  'requests.read',
  'requests.create',
  'requests.create_billing',
  'requests.approve',
  'inbox.read',
  'decisions.read',
  'decisions.approve',
  'hours.read',
  'hours.update',
  'notices.read',
  'notices.create',
  'reports.read',
  'reports.share',
  'team.read',
  'team.manage',
  'team.invite_crew',
  'billing.read',
  'billing.manage',
  'billing.cancel',
  'export.create',
  'export.read',
] as const;
export const ClientAction = z.enum(CLIENT_ACTIONS);
export type ClientAction = z.infer<typeof ClientAction>;

/**
 * yes: allowed. no: refused. own: allowed only on resources the user created (crew review requests, jobs,
 * requests). approver: managers only when the owner ticked "can approve" (docs/50 50-D9).
 */
export type Grant = 'yes' | 'no' | 'own' | 'approver';

export interface ClientRule {
  owner: Grant;
  manager: Grant;
  crew: Grant;
  bookkeeper: Grant;
  /** A passkey assertion or fresh email code within the last 10 minutes (docs/53 §4.2). */
  stepUp: boolean;
  /** What a refusal returns: 404 hides existence (reads of tenant data), 403 explains itself (docs/52 §5). */
  deny: 403 | 404;
}

const r = (owner: Grant, manager: Grant, crew: Grant, bookkeeper: Grant, stepUp = false, deny: 403 | 404 = 403): ClientRule => ({
  owner,
  manager,
  crew,
  bookkeeper,
  stepUp,
  deny,
});

/** docs/53 §4.3, one row per action. Location scoping applies on top of every row (docs/53 §4.3 last line). */
export const CLIENT_PERMISSIONS: Record<ClientAction, ClientRule> = {
  'home.read': r('yes', 'yes', 'no', 'no'),
  'activity.read': r('yes', 'yes', 'no', 'no'),
  'locations.read': r('yes', 'yes', 'yes', 'yes'),
  'onboarding.read': r('yes', 'yes', 'no', 'no'),
  'onboarding.update': r('yes', 'yes', 'no', 'no'),
  'golive.approve': r('yes', 'approver', 'no', 'no', true),
  'facts.read': r('yes', 'yes', 'no', 'no'),
  'facts.confirm': r('yes', 'yes', 'no', 'no'),
  'domain.check': r('yes', 'yes', 'no', 'no'),
  'leads.read': r('yes', 'yes', 'no', 'no', false, 404),
  'leads.update': r('yes', 'yes', 'no', 'no', false, 404),
  'leads.export': r('yes', 'yes', 'no', 'no'),
  'events.read': r('yes', 'yes', 'no', 'no'),
  'reviews.read': r('yes', 'yes', 'yes', 'no'),
  'reviews.reply': r('yes', 'yes', 'no', 'no'),
  'review_requests.read': r('yes', 'yes', 'own', 'no', false, 404),
  'review_requests.create': r('yes', 'yes', 'own', 'no'),
  'review_requests.bulk': r('yes', 'yes', 'no', 'no'),
  'review_requests.cancel': r('yes', 'yes', 'own', 'no', false, 404),
  /** The review ask emails are the owner's words (deck ruling c131). */
  'review_templates.edit': r('yes', 'no', 'no', 'no'),
  /** Marking a week or month of "your two jobs" as having had no jobs (r01): the owner or a manager. */
  'two_jobs.mark': r('yes', 'yes', 'no', 'no'),
  'messaging.read': r('yes', 'yes', 'yes', 'no'),
  'jobs.read': r('yes', 'yes', 'own', 'no', false, 404),
  'jobs.create': r('yes', 'yes', 'yes', 'no'),
  'jobs.update': r('yes', 'yes', 'own', 'no', false, 404),
  'media.read': r('yes', 'yes', 'own', 'no', false, 404),
  /** Retitle, favourite, say what a photo shows, "don't use this one" and remove (k11). Crew: their own photos. */
  'media.update': r('yes', 'yes', 'own', 'no', false, 404),
  'uploads.create': r('yes', 'yes', 'yes', 'no'),
  'requests.read': r('yes', 'yes', 'own', 'own', false, 404),
  'requests.create': r('yes', 'yes', 'yes', 'no'),
  'requests.create_billing': r('yes', 'yes', 'yes', 'yes'),
  'requests.approve': r('yes', 'approver', 'no', 'no'),
  'inbox.read': r('yes', 'yes', 'own', 'own'),
  'decisions.read': r('yes', 'approver', 'no', 'no', false, 404),
  'decisions.approve': r('yes', 'approver', 'no', 'no', true),
  'hours.read': r('yes', 'yes', 'no', 'no'),
  'hours.update': r('yes', 'yes', 'no', 'no'),
  'notices.read': r('yes', 'yes', 'no', 'no'),
  'notices.create': r('yes', 'yes', 'no', 'no'),
  'reports.read': r('yes', 'yes', 'no', 'no'),
  'reports.share': r('yes', 'yes', 'no', 'no'),
  'team.read': r('yes', 'yes', 'no', 'no'),
  'team.manage': r('yes', 'no', 'no', 'no', true),
  'team.invite_crew': r('yes', 'yes', 'no', 'no'),
  'billing.read': r('yes', 'no', 'no', 'yes'),
  'billing.manage': r('yes', 'no', 'no', 'yes', true),
  'billing.cancel': r('yes', 'no', 'no', 'no', true),
  'export.create': r('yes', 'no', 'no', 'no', true),
  'export.read': r('yes', 'no', 'no', 'no', true),
};

/** Staff actions (docs/50 §5, docs/52 §6). Every one is role-checked in the Worker and audited. */
export const STAFF_ACTIONS = [
  'today.read',
  'clients.read',
  'client.read',
  'view_as.open',
  'view_as.break_glass',
  'leads.read_contents',
  'contacts.read',
  'requests.read',
  'requests.approve_t1_t3',
  'requests.approve_any',
  'requests.edit',
  'requests.assign',
  'content.read',
  'content.approve',
  'media.read',
  'media.review',
  'gbp_tasks.read',
  'gbp_tasks.complete',
  'onboarding.read',
  'onboarding.manage',
  'domains.read',
  'domains.manage',
  'messaging.read',
  'messaging.manage',
  'publishing.read',
  'publishing.approve',
  'publishing.rollback',
  'billing.read',
  'billing.credit',
  'billing.refund',
  'health.read',
  'health.retry',
  'audit.read',
  'audit.export',
  'exports.create',
  'engine.read',
  'rules.read',
  'rules.edit',
  'incidents.read',
  'incidents.freeze',
  'staff.manage',
  'scans.read',
  'scans.manage',
  'reports.read',
  'reports.publish',
] as const;
export const StaffAction = z.enum(STAFF_ACTIONS);
export type StaffAction = z.infer<typeof StaffAction>;

/** Staff roles from most to least trusted. `STAFF_RANK[a] >= STAFF_RANK[b]` means a can do what b can. */
export const STAFF_RANK: Record<z.infer<typeof StaffRole>, number> = { owner: 5, admin: 4, operator: 3, va: 2, viewer: 1 };

export interface StaffRule {
  /** Lowest role allowed. */
  min: z.infer<typeof StaffRole>;
  /** Fresh Access login required (exports, refunds, freezes: docs/53 §4.4). */
  reauth: boolean;
  /** Writes a reason with the audit row (docs/53 §4.4: reading lead contents or contacts). */
  reason: boolean;
}

const s = (min: z.infer<typeof StaffRole>, reauth = false, reason = false): StaffRule => ({ min, reauth, reason });

export const STAFF_PERMISSIONS: Record<StaffAction, StaffRule> = {
  'today.read': s('viewer'),
  'clients.read': s('viewer'),
  'client.read': s('viewer'),
  'view_as.open': s('va', false, true),
  'view_as.break_glass': s('owner', false, false),
  'leads.read_contents': s('operator', false, true),
  'contacts.read': s('operator', false, true),
  'requests.read': s('viewer'),
  'requests.approve_t1_t3': s('operator'),
  'requests.approve_any': s('admin'),
  'requests.edit': s('operator'),
  'requests.assign': s('va'),
  'content.read': s('viewer'),
  'content.approve': s('va'),
  /** The photo queue and a client's library (k11): approve, turn down with a reason, set the focal point, place in a slot. */
  'media.read': s('viewer'),
  'media.review': s('va'),
  'gbp_tasks.read': s('viewer'),
  'gbp_tasks.complete': s('va'),
  'onboarding.read': s('viewer'),
  'onboarding.manage': s('va'),
  'domains.read': s('viewer'),
  'domains.manage': s('operator'),
  'messaging.read': s('viewer'),
  'messaging.manage': s('operator'),
  'publishing.read': s('viewer'),
  'publishing.approve': s('operator'),
  'publishing.rollback': s('operator'),
  'billing.read': s('operator'),
  'billing.credit': s('admin'),
  'billing.refund': s('admin', true),
  'health.read': s('viewer'),
  'health.retry': s('operator'),
  'audit.read': s('operator'),
  'audit.export': s('admin', true),
  /** A client export started by staff, which may carry the site's files (c013, staff only): a fresh login; the body carries the reason, kept as a client note. */
  'exports.create': s('admin', true),
  'engine.read': s('admin'),
  'rules.read': s('viewer'),
  'rules.edit': s('admin'),
  'incidents.read': s('viewer'),
  'incidents.freeze': s('admin', true),
  'staff.manage': s('owner', true),
  /** Scans cost money and the grid is part of a promise: reading is open, changing needs an operator. */
  'scans.read': s('viewer'),
  'scans.manage': s('operator'),
  'reports.read': s('viewer'),
  /** Editing the words, holding and publishing a client's report. */
  'reports.publish': s('operator'),
};

/** Tabs each client role sees (docs/52 §2.1). */
export const ROLE_TABS: Record<z.infer<typeof ClientRole>, readonly ('home' | 'leads' | 'reviews' | 'jobs' | 'inbox' | 'account')[]> = {
  owner: ['home', 'leads', 'reviews', 'jobs', 'inbox', 'account'],
  manager: ['home', 'leads', 'reviews', 'jobs', 'inbox', 'account'],
  crew: ['reviews', 'jobs', 'account'],
  bookkeeper: ['account'],
};
